// packages/components/src/agent_profile/showcase/BundlesTabs.tsx
import React, { useMemo } from 'react';
import { Card } from '@dopomogai/ui';
import type { FeatureItem } from '@dopomogai/types';
import { TierSelector } from './TierSelector';
import { EtaPill } from './EtaPill';

type TierKey = 'trainee' | 'junior' | 'middle' | 'senior';

export type TierBundleLite = {
  key: TierKey;
  title: string;
  description: string;
  included_feature_keys: string[];
  eta_days_min: number;
  eta_days_max: number;
};

export type IndustryPresetDelta = {
  add_feature_keys?: string[];
  remove_feature_keys?: string[];
};

// Accept Record or Map
type CatalogMap = Record<string, FeatureItem> | Map<string, FeatureItem>;

type Props = {
  bundles: TierBundleLite[];
  catalog: CatalogMap;
  selectedTier: TierKey;
  onTierChange: (tier: TierKey) => void;
  industryPreset?: IndustryPresetDelta;
  onCustomize: (tier: TierKey) => void;
};

function getCatalogItem(catalog: CatalogMap, key: string): FeatureItem | undefined {
  // Supports Map and plain object
  if (catalog && typeof (catalog as any).get === 'function') {
    return (catalog as Map<string, FeatureItem>).get(key);
  }
  return (catalog as Record<string, FeatureItem>)[key];
}

function computeBundleFeatures(bundle: TierBundleLite, preset?: IndustryPresetDelta): string[] {
  const base = new Set(bundle.included_feature_keys || []);
  if (preset?.add_feature_keys) preset.add_feature_keys.forEach((k) => base.add(k));
  if (preset?.remove_feature_keys) preset.remove_feature_keys.forEach((k) => base.delete(k));
  return Array.from(base);
}

function groupByCategory(keys: string[], catalog: CatalogMap) {
  const map: Record<string, FeatureItem[]> = {};
  keys.forEach((k) => {
    const item = getCatalogItem(catalog, k);
    if (!item) return;
    const cat =
      // be robust to different shapes
      (item as any).category ||
      (item as any).category_name ||
      (item as any).category_label ||
      'Features';
    map[cat] = map[cat] || [];
    map[cat].push(item);
  });
  // sort by tier gate for readability
  Object.keys(map).forEach((cat) => {
    map[cat].sort((a, b) => tierRank(a.tier_gate) - tierRank(b.tier_gate));
  });
  return map;
}

function tierRank(t: FeatureItem['tier_gate']) {
  switch (t) {
    case 'trainee': return 0;
    case 'junior': return 1;
    case 'middle': return 2;
    case 'senior': return 3;
    default: return 99;
  }
}

const PREV_TIER: Record<TierKey, TierKey | null> = {
  trainee: null,
  junior: 'trainee',
  middle: 'junior',
  senior: 'middle',
};

export function BundlesTabs({
  bundles,
  catalog,
  selectedTier,
  onTierChange,
  industryPreset,
  onCustomize,
}: Props) {
  const currentBundle = useMemo(
    () => bundles.find((b) => b.key === selectedTier),
    [bundles, selectedTier]
  );

  const featureKeys = useMemo(
    () => (currentBundle ? computeBundleFeatures(currentBundle, industryPreset) : []),
    [currentBundle, industryPreset]
  );

  const grouped = useMemo(() => groupByCategory(featureKeys, catalog), [featureKeys, catalog]);

  const tierOptions = useMemo(
    () =>
      bundles.map((b) => ({
        key: b.key,
        title: b.title,
        oneLiner: b.description || undefined,
      })),
    [bundles]
  );

  const prevTierKey: TierKey | null = PREV_TIER[selectedTier];
  const prevBundleTitle = useMemo(() => {
    if (!prevTierKey) return null;
    const prev = bundles.find((b) => b.key === prevTierKey);
    return prev?.title || (prevTierKey.charAt(0).toUpperCase() + prevTierKey.slice(1));
  }, [bundles, prevTierKey]);

  return (
    <Card>
      <div className="p-5">
        {/* Helper line explaining tiers */}
        <div className="text-xs md:text-sm text-slate-700 mb-2">
          Select your agent level — higher tiers include everything from the previous tier.
        </div>

        {/* Segmented Tier selector; microcopy hidden to avoid duplication */}
        <TierSelector value={selectedTier} onChange={onTierChange} tiers={tierOptions as any} showMicrocopy={false} />

        {currentBundle && (
          <>
            {/* Single consolidated description row with a prominent ETA pill */}
            <div className="mt-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div className="text-sm text-slate-700">
                <span className="font-semibold text-slate-900">{currentBundle.title}:</span>{' '}
                {currentBundle.description}
              </div>
              <EtaPill
                minDays={currentBundle.eta_days_min}
                maxDays={currentBundle.eta_days_max}
                size="md"
              />
            </div>

            {/* Explicit inclusion note for non-base tiers */}
            {prevBundleTitle && (
              <div className="mt-2 inline-flex items-center gap-2 bg-slate-50 text-slate-900 border-2 border-black rounded-lg px-3 py-1.5 text-xs md:text-sm">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-700" />
                <span>
                  Includes everything in <span className="font-semibold">{prevBundleTitle}</span>, plus:
                </span>
              </div>
            )}

            {/* Features grouped by category */}
            <div className="mt-4 space-y-4">
              {Object.keys(grouped).length === 0 && (
                <div className="text-sm text-slate-600">
                  This bundle has no visible features yet.
                </div>
              )}
              {Object.entries(grouped).map(([cat, items]) => (
                <div key={cat}>
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{cat}</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {items.map((f) => (
                      <div
                        id={`feature-${f.key}`}
                        key={f.key}
                        className="bg-white border-2 border-black rounded-lg p-3 text-sm flex items-start gap-2"
                      >
                        <span className="mt-0.5 inline-block w-2 h-2 rounded-full bg-slate-700" />
                        <div>
                          <div className="font-semibold text-slate-800">{f.title}</div>
                          <div className="text-xs text-slate-600">
                            Tier: {f.tier_gate} • {f.eta_days_from}–{f.eta_days_to} days
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5">
              <button
                onClick={() => onCustomize(currentBundle.key)}
                className="bg-emerald-500 text-white font-extrabold border-2 border-black rounded-lg px-4 py-2 transform transition-all hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgb(17,24,39)] active:translate-x-0 active:translate-y-0 active:shadow-none"
                aria-label={`Customize the ${currentBundle.title} bundle`}
              >
                Customize this bundle
              </button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

export default BundlesTabs;
